/* ============================================================================
   TrainerFields - the trainer name and level, as one compact row.
   ----------------------------------------------------------------------------
   Two typed fields are all the app asks a trainer for (lib/trainerSetup), so
   they sit side by side: a name box and a short level box. Owner direction,
   2026-10-02: the step that asked for them filled most of the screen for two
   values and looked cheap. One row, wherever it appears:

     - the trainer step (AuthModal's TrainerSetup): sign-up, Live Scan's "Add
       my trainer name", and the once-per-account confirm for trainers from
       the screenshot builds;
     - Settings > Account, where the row IS the editor (TrainerInlineEditor),
       with a tick that appears when something changed. No dialog.

   The form rules live in useTrainerForm so both faces share them.
   ============================================================================ */
import { useEffect, useRef, useState, type RefObject } from "react";
import { AtSign, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "./AuthProvider";
import { authErrorMessage } from "@/lib/apiError";
import { queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { sanitizeTrainerNameInput, validateTrainerName } from "@/lib/trainerSetup";
import { TRAINER_LEVEL_MAX, TRAINER_LEVEL_MIN, validateTrainerLevelInput } from "@/lib/trainerLevel";

/**
 * Bring a just-focused field clear of the keyboard.
 *
 * The iOS keyboard OVERLAYS the page (Keyboard.resize 'none'), so the trainer
 * step's dialog is centred and capped in the space above it and scrolls
 * internally when that space is shorter than the form. This moves the focused
 * field to the middle of what is left; the delay waits out the keyboard
 * slide-in.
 *
 * Deliberately NOT `behavior: "smooth"`: a smooth programmatic scroll swallows
 * touch scrolling on iOS while it runs, and a field still gliding when a second
 * tap lands never gets its Paste callout. One instant move.
 */
function revealFieldAboveKeyboard(el: HTMLElement | null): void {
  if (!el) return;
  window.setTimeout(() => {
    try {
      el.scrollIntoView({ block: "center" });
    } catch {
      /* jsdom / older WebViews without scrollIntoView options: not worth a fallback. */
    }
  }, 300);
}

export interface TrainerForm {
  trainerName: string;
  level: string;
  setTrainerName: (raw: string) => void;
  setLevel: (raw: string) => void;
  busy: boolean;
  error: string | null;
  /** What is still missing, in field order, as user-facing copy. */
  missing: string[];
  /** Both fields are valid and nothing is in flight. */
  canSubmit: boolean;
  /** The fields differ from what is saved. */
  dirty: boolean;
  /** Either field has been typed in since it was last in step with the account. */
  touched: boolean;
  /** Saves when there is something to send. Resolves true when the form is done. */
  save: () => Promise<boolean>;
  nameRef: RefObject<HTMLInputElement>;
  levelRef: RefObject<HTMLInputElement>;
}

/**
 * The trainer name and level form.
 *
 * `confirming`: the trainer is being asked to type (or confirm) who they are,
 * rather than editing a profile they already typed. The save always goes to
 * the server, even when nothing changed, because the save itself is the
 * answer; and it sends the name as the box shows it.
 */
export function useTrainerForm({ confirming }: { confirming: boolean }): TrainerForm {
  const { confirmProfile, account } = useAuth();

  // THE SAVED LEVEL IS THE HIGHER OF TWO ROWS. The account row and the trainer
  // profile row both hold one, and for trainers from the screenshot builds
  // they disagree: only the FIRST import wrote the account row, every later
  // re-upload moved the profile row alone. Prefilling from the account showed
  // (and a name-only save then wrote back) a level the trainer left behind
  // months ago. Levels only go up, so the higher one is the current one.
  const profileLevel =
    queryClient.getQueryData<{ trainer: { level?: number | null } | null }>(["/api/settings/all"])
      ?.trainer?.level ?? null;
  const savedLevel = Math.max(account?.trainerLevel ?? 0, profileLevel ?? 0) || null;
  const savedName = (account?.trainerName ?? "").trim();

  // Prefilled, so the form opens on what is saved and a trainer who only wants
  // to bump their level types one number.
  const [trainerName, setTrainerNameState] = useState(() => sanitizeTrainerNameInput(savedName));
  // Set by the first keystroke or paste in the name box, see `nameToSend` below.
  const [nameEdited, setNameEdited] = useState(false);
  const [level, setLevelState] = useState(() => (savedLevel != null ? String(savedLevel) : ""));
  const [levelEdited, setLevelEdited] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const nameRef = useRef<HTMLInputElement>(null);
  const levelRef = useRef<HTMLInputElement>(null);

  const touched = nameEdited || levelEdited;

  // Follow the account while the trainer has not typed anything: the saved
  // level can arrive after the first render (the profile row is a query), and
  // a save elsewhere must not leave a stale value sitting in an idle form. A
  // form someone is typing in is never overwritten under their fingers.
  useEffect(() => {
    if (touched) return;
    setTrainerNameState(sanitizeTrainerNameInput(savedName));
    setLevelState(savedLevel != null ? String(savedLevel) : "");
  }, [savedName, savedLevel, touched]);

  const nameCheck = validateTrainerName(trainerName);
  const levelCheck = validateTrainerLevelInput(level);
  // Both are asked for. The level validator treats blank as "no value" (it also
  // serves the clear-my-level control), so blank is turned away here.
  const levelValue = levelCheck.ok ? levelCheck.value : null;

  const missing: string[] = [];
  if (!nameCheck.ok) missing.push(nameCheck.reason);
  if (level.trim() === "") missing.push("Enter your level");
  else if (!levelCheck.ok) missing.push(`Levels run from ${TRAINER_LEVEL_MIN} to ${TRAINER_LEVEL_MAX}`);

  const canSubmit = !busy && missing.length === 0;

  // A SAVED NAME THE TRAINER DID NOT TOUCH GOES BACK AS IT IS SAVED, when they
  // are only editing. Names saved before 2026-10-02 came from a free-text
  // field and can hold a space, punctuation or more than 15 characters; the
  // box shows the cleaned form. A trainer who opens Settings only to bump
  // their level must not be renamed by that (the server re-derives the handle
  // when the name changes). Any edit to the box, including retyping the
  // cleaned form, sends what it shows. A trainer who is CONFIRMING their name
  // is agreeing to what the box shows, so that is what is sent.
  const nameToSend = nameCheck.ok
    ? !confirming && savedName.length > 0 && !nameEdited
      ? savedName
      : nameCheck.value
    : "";

  const unchanged =
    nameToSend === savedName &&
    levelValue === (account?.trainerLevel ?? null) &&
    (profileLevel == null || profileLevel === levelValue);
  const dirty = canSubmit && !unchanged;

  const save = async (): Promise<boolean> => {
    if (!canSubmit || !nameCheck.ok || levelValue == null) return false;
    // Nothing changed (the editor, saved as it stood): there is nothing to
    // send, and a save is not a new reading of the profile. Only when both
    // level rows already hold this level; otherwise the save is what brings
    // them back in line.
    if (!confirming && unchanged) return true;
    setBusy(true);
    setError(null);
    try {
      // Name and level only: the server reads a confirm with no screenshot as
      // typed (profileEntrySource) and takes the level as it stands.
      await confirmProfile({ trainer_name: nameToSend, level: levelValue });
      setNameEdited(false);
      setLevelEdited(false);
      setBusy(false);
      return true;
    } catch (ex: unknown) {
      setError(authErrorMessage(ex));
      setBusy(false);
      return false;
    }
  };

  return {
    trainerName,
    level,
    setTrainerName: (raw) => {
      setNameEdited(true);
      setTrainerNameState(sanitizeTrainerNameInput(raw));
    },
    setLevel: (raw) => {
      setLevelEdited(true);
      setLevelState(raw.replace(/[^0-9]/g, "").slice(0, 2));
    },
    busy,
    error,
    missing,
    canSubmit,
    dirty,
    touched,
    save,
    nameRef,
    levelRef,
  };
}

// The boxes are the inputs themselves: the theme styles every <input> (its
// background and border colour, light and dark, index.css `.theme-v2 input`),
// so a wrapper drawn as the box ended up with a second, differently coloured
// box inside it. The "@" and "LV" marks sit inside the input's left padding.
const FIELD_INPUT =
  "w-full h-11 rounded-xl border text-sm outline-none placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring";

/**
 * The row itself: the name box, and a short level box beside it.
 *
 * Reads the same on the trainer step's card and on a Settings card, in light
 * and dark.
 */
export function TrainerFieldsRow({ form, idPrefix }: { form: TrainerForm; idPrefix: string }) {
  return (
    <>
      <div className="relative flex-1 min-w-0">
        <AtSign
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground"
          aria-hidden
        />
        <input
          ref={form.nameRef}
          id={`${idPrefix}-name`}
          data-testid="input-trainer-name"
          aria-label="Trainer name"
          value={form.trainerName}
          onChange={(e) => form.setTrainerName(e.target.value)}
          onFocus={() => revealFieldAboveKeyboard(form.nameRef.current)}
          onKeyDown={(e) => {
            // Return on the name moves to the level instead of submitting a
            // half-filled form.
            if (e.key === "Enter" && form.level.trim() === "") {
              e.preventDefault();
              form.levelRef.current?.focus();
            }
          }}
          placeholder="Trainer name"
          // No maxLength: the browser would clip a paste to 15 BEFORE the
          // sanitiser drops its spaces, cutting the tail off a real name.
          // The sanitiser caps the cleaned text instead.
          autoCapitalize="none"
          autoCorrect="off"
          autoComplete="off"
          spellCheck={false}
          enterKeyHint="next"
          required
          className={`${FIELD_INPUT} pl-9 pr-3`}
        />
      </div>
      <div className="relative shrink-0 w-[5.5rem]">
        <span
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground"
          aria-hidden
        >
          Lv
        </span>
        <input
          ref={form.levelRef}
          id={`${idPrefix}-level`}
          data-testid="input-level"
          aria-label="Level"
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          value={form.level}
          onChange={(e) => form.setLevel(e.target.value)}
          onFocus={() => revealFieldAboveKeyboard(form.levelRef.current)}
          placeholder="1-80"
          autoComplete="off"
          enterKeyHint="done"
          required
          className={`${FIELD_INPUT} pl-9 pr-3 font-mono tabular-nums text-right`}
        />
      </div>
    </>
  );
}

/**
 * Settings > Account: the row is the editor.
 *
 * The saved name and level sit in the boxes; typing in either brings up the
 * tick, which saves both. One line under it says what the row is, or what is
 * wrong with what was typed.
 */
export function TrainerInlineEditor() {
  const form = useTrainerForm({ confirming: false });
  const { toast } = useToast();
  const problem = form.error ?? (form.touched && form.missing.length > 0 ? `${form.missing[0]}.` : null);
  return (
    <form
      className="mt-1.5"
      data-testid="form-settings-trainer"
      onSubmit={async (e) => {
        e.preventDefault();
        if (!form.dirty) return;
        if (await form.save()) toast({ title: "Trainer name and level saved" });
      }}
    >
      <div className="flex items-center gap-2">
        <TrainerFieldsRow form={form} idPrefix="settings-trainer" />
        <Button
          type="submit"
          size="sm"
          disabled={!form.dirty || form.busy}
          aria-label="Save trainer name and level"
          data-testid="button-settings-save-trainer"
        >
          {form.busy ? "Saving" : <Check className="w-3.5 h-3.5" />}
        </Button>
      </div>
      <p
        className={`mt-1 text-[11px] leading-snug ${problem ? "text-destructive" : "text-muted-foreground"}`}
        data-testid={problem ? "text-settings-trainer-problem" : undefined}
      >
        {problem ?? "As they appear in Pokémon GO. Friends find you by this name."}
      </p>
    </form>
  );
}
